import { type FormEvent, useState } from "react";
import { createUser, getUsersApi } from "@praesto/core";
import { Modal, Button, Input, Select, FormField } from "@praesto/ui-kit";
import { ROLE_LABEL, ROLE_ORDER } from "./roleLabels";
import { DepartmentMultiSelect } from "./DepartmentMultiSelect";

export function CreateUserModal(props: {
  hotels: ReadonlyArray<{ id: string; name: string }>;
  onClose: () => void;
  onDone: (uid: string, password: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<
    "tenant_owner" | "manager_hotel" | "manager_functional" | "staff"
  >("staff");
  const [hotelId, setHotelId] = useState(props.hotels[0]?.id ?? "");
  const [departments, setDepartments] = useState<ReadonlyArray<string>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function generate() {
    const charset = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let out = "";
    for (let i = 0; i < 14; i++) {
      out += charset[Math.floor(Math.random() * charset.length)];
    }
    setPassword(out);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      const r = await createUser(
        api.apiBase,
        {
          email,
          password,
          displayName,
          role,
          hotelId,
          departments: [...departments],
        },
        tok,
      );
      props.onDone(r.uid, password);
    } catch (e2) {
      setError(String(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={props.onClose} title="Crea utente con password">
      <form onSubmit={submit} className="space-y-3">
        <FormField label="Email" required>
          <Input
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            value={email}
            required
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormField>
        <FormField label="Nome" required>
          <Input value={displayName} required onChange={(e) => setDisplayName(e.target.value)} />
        </FormField>
        <FormField label="Password (minimo 12 caratteri, lettere e numeri)" required>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              type="text"
              value={password}
              required
              minLength={12}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="button"
              variant="ghost"
              className="shrink-0 w-full sm:w-auto"
              onClick={generate}
            >
              Genera
            </Button>
          </div>
        </FormField>
        <FormField label="Ruolo">
          <Select value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
            {ROLE_ORDER.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Hotel" required>
          <Select
            value={hotelId}
            required
            onChange={(e) => {
              setHotelId(e.target.value);
              setDepartments([]); // codes are hotel-specific — reset on hotel change
            }}
          >
            {props.hotels.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Reparti">
          <DepartmentMultiSelect hotelId={hotelId} value={departments} onChange={setDepartments} />
        </FormField>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            className="w-full sm:w-auto"
            onClick={props.onClose}
          >
            Annulla
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="w-full sm:w-auto"
            loading={busy}
            disabled={busy || password.length < 12}
          >
            Crea
          </Button>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  );
}
